import { useController, useFormContext } from "react-hook-form";
import {
  useFieldProps,
  FieldBaseProps,
} from "@shared/features/form/useFieldProps";
import { RadioGroupItem } from "./RadioGroupItem";

export type Props = FieldBaseProps & {
  name: string;
  options: {
    value: number;
    label: string;
  }[];
  maxItems?: number;
  disabled?: boolean;
  id?: string;
};

export function RadioGroup({
  name,
  options,
  maxItems = Infinity,
  required = false,
  disabled = false,
  error,
  isLoading,
  isSubmitting,
  className,
  id,
}: Props) {
  const isMultiSelect = maxItems > 1;

  const form = useFormContext();

  const {
    field: { value = [], onChange, onBlur },
  } = useController({
    name,
    control: form.control,
    defaultValue: [],
    rules: {
      required: required ? "This field is required" : false,
    },
  });

  const selectedValues: number[] = Array.isArray(value) ? value : [];

  const handleChange = (optionValue: number, checked: boolean) => {
    let newValue: number[];

    if (checked) {
      if (isMultiSelect) {
        if (selectedValues.length >= maxItems) return;
        newValue = [...selectedValues, optionValue];
      } else {
        newValue = [optionValue];
      }
    } else {
      newValue = selectedValues.filter((val) => val !== optionValue);
    }

    onChange(newValue);
  };

  const fieldProps = useFieldProps({
    className,
    error,
    isLoading,
    isSubmitting,
    required,
  });

  return (
    <div
      {...fieldProps}
      className="border-none"
      role="group"
      aria-labelledby={`${id}-label`}
    >
      <div className="flex flex-row flex-wrap gap-4" onBlur={onBlur}>
        {options.map((option) => {
          const { value, label, description } = option;

          const isChecked = selectedValues.includes(value);

          const isDisabled =
            disabled || (!isChecked && selectedValues.length >= maxItems);

          return (
            <RadioGroupItem
              key={value}
              label={label}
              description={description}
              checked={isChecked}
              disabled={isDisabled}
              onChange={(checked) => handleChange(value, checked)}
              className="w-full md:flex-[0_0_calc(50%-0.5rem)]"
            />
          );
        })}
      </div>
    </div>
  );
}

export default RadioGroup;
